type FetchRequest = Parameters<typeof $fetch>[0]
type FetchResponse = Awaited<ReturnType<typeof $fetch>>

const AUTH_FETCH_PATCH_FLAG = '__ap_auth_fetch_patched__'
const AUTH_RETRY_HEADER = 'x-ap-auth-retry'
const LINK_RENEW_MARGIN_MS = 30_000

function getStatusCode(error: any): number | null {
  const statusCode = Number(
    error?.statusCode
    ?? error?.status
    ?? error?.response?.status
    ?? error?.data?.statusCode,
  )

  return Number.isFinite(statusCode) ? statusCode : null
}

function isAuthError(error: any): boolean {
  const statusCode = getStatusCode(error)
  if (statusCode === 401) {
    return true
  }

  const message = String(
    error?.data?.message
    ?? error?.message
    ?? error?.statusMessage
    ?? '',
  ).toLowerCase()

  return (
    message.includes('token invalide')
    || message.includes('token expir')
    || message.includes('non authentifi')
    || message.includes('unauthorized')
  )
}

function isVolunteerRoute(path: string | undefined): boolean {
  return Boolean(path?.startsWith('/volontaires'))
}

function isApiRequest(request: FetchRequest): boolean {
  if (typeof request === 'string') {
    return request.startsWith('/api/')
  }

  if (request instanceof Request) {
    try {
      const url = new URL(request.url, window.location.origin)
      return url.pathname.startsWith('/api/')
    }
    catch {
      return false
    }
  }

  return false
}

// Appels directs à l'API Directus (useDirectusItems, ...) : URL absolue sous l'URL Directus, hors routes d'authentification
function isDirectusDataRequest(request: FetchRequest, directusUrl: string): boolean {
  if (typeof request !== 'string' || !directusUrl || !request.startsWith(directusUrl)) {
    return false
  }

  const path = request.slice(directusUrl.length)
  return !path.startsWith('/auth/') && !path.startsWith('/account-security/')
}

function getHeaderValue(headers: HeadersInit | undefined, key: string): string | undefined {
  if (!headers) {
    return undefined
  }

  if (headers instanceof Headers) {
    return headers.get(key) ?? undefined
  }

  if (Array.isArray(headers)) {
    const entry = headers.find(([name]) => name.toLowerCase() === key.toLowerCase())
    return entry?.[1]
  }

  const record = headers as Record<string, string>
  const matchedKey = Object.keys(record).find(name => name.toLowerCase() === key.toLowerCase())
  return matchedKey ? record[matchedKey] : undefined
}

function withRetryHeader(options: Record<string, any> | undefined, token?: string | null): Record<string, any> {
  const nextOptions = { ...(options || {}) }
  const currentHeaders = nextOptions.headers

  // Appel Directus direct : le jeton est dans l'en-tête Authorization, à remplacer par le jeton renouvelé
  if (token && currentHeaders) {
    const bearer = `Bearer ${token}`
    if (currentHeaders instanceof Headers) {
      const headers = new Headers(currentHeaders)
      headers.set('Authorization', bearer)
      nextOptions.headers = headers
    }
    else if (Array.isArray(currentHeaders)) {
      nextOptions.headers = currentHeaders.filter(([name]) => name.toLowerCase() !== 'authorization').concat([['Authorization', bearer]])
    }
    else {
      const record = { ...(currentHeaders as Record<string, string>) }
      Object.keys(record).filter(name => name.toLowerCase() === 'authorization').forEach(name => delete record[name])
      nextOptions.headers = { ...record, Authorization: bearer }
    }
  }

  if (currentHeaders instanceof Headers) {
    const headers = new Headers(currentHeaders)
    headers.set(AUTH_RETRY_HEADER, '1')
    nextOptions.headers = headers
    return nextOptions
  }

  if (Array.isArray(currentHeaders)) {
    nextOptions.headers = [...currentHeaders, [AUTH_RETRY_HEADER, '1']]
    return nextOptions
  }

  nextOptions.headers = {
    ...(currentHeaders || {}),
    [AUTH_RETRY_HEADER]: '1',
  }

  return nextOptions
}

export default defineNuxtPlugin((nuxtApp) => {
  const globalWithFlag = globalThis as typeof globalThis & Record<string, boolean>
  if (globalWithFlag[AUTH_FETCH_PATCH_FLAG]) {
    return
  }

  const baseFetch = globalThis.$fetch
  if (!baseFetch) {
    return
  }

  globalWithFlag[AUTH_FETCH_PATCH_FLAG] = true

  const trust = useDeviceTrust()
  const userStore = useUserStore()
  const router = useRouter()
  const directusUrl = useDirectusUrl()
  const { token, expires } = useDirectusToken()
  // Calcul direct : les computed de useDirectusToken ne dépendent pas de l'heure et peuvent rester figés
  const msLeft = () => Math.max(0, (expires.value ?? 0) - Date.now())
  const isTokenExpired = () => !token.value || msLeft() === 0

  let refreshPromise: Promise<unknown> | null = null

  const refreshSessionIfNeeded = async (): Promise<void> => {
    if (!refreshPromise) {
      refreshPromise = trust.renewSession().finally(() => {
        refreshPromise = null
      })
    }

    if (!(await refreshPromise)) {
      throw new Error('SESSION_EXPIRED')
    }
  }

  const redirectToLogin = async () => {
    trust.endSession()
    userStore.logout()
    await navigateTo('/volontaires/connexion')
  }

  const wrappedFetch = (async (request: FetchRequest, options?: Record<string, any>): Promise<FetchResponse> => {
    const requestOptions = options ?? {}
    const hasRetried = getHeaderValue(requestOptions.headers, AUTH_RETRY_HEADER) === '1'

    if (hasRetried) {
      return await baseFetch(request, requestOptions as any)
    }

    const isDirectus = isDirectusDataRequest(request, directusUrl)

    // useDirectusItems n'envoie aucun jeton une fois celui-ci expiré (accès public, 403) : on le renouvelle avant l'appel
    if (isDirectus && token.value && isTokenExpired() && isVolunteerRoute(router.currentRoute.value?.path)) {
      try {
        await refreshSessionIfNeeded()
        return await baseFetch(request, withRetryHeader(requestOptions, token.value) as any)
      }
      catch (refreshError: any) {
        if (refreshError?.message === 'SESSION_EXPIRED') {
          await redirectToLogin()
        }
        throw refreshError
      }
    }

    try {
      return await baseFetch(request, requestOptions as any)
    }
    catch (error: any) {
      const currentPath = router.currentRoute.value?.path
      const canRetryAuth = isVolunteerRoute(currentPath) && (isApiRequest(request) || isDirectus)

      if (!canRetryAuth || !isAuthError(error)) {
        throw error
      }

      try {
        await refreshSessionIfNeeded()
        // Laisse le temps aux cookies (écrits par un watcher Vue) d'être à jour avant de rejouer l'appel
        await nextTick()
        return await baseFetch(request, withRetryHeader(requestOptions, isDirectus ? token.value : null) as any)
      }
      catch (refreshError: any) {
        // Panne réseau ou serveur : on ne déconnecte pas, l'erreur d'origine remonte
        if (refreshError?.message === 'SESSION_EXPIRED') {
          await redirectToLogin()
        }
        throw refreshError
      }
    }
  }) as typeof baseFetch

  // Liens directs vers l'API (PDF, exports, pièces jointes) : le navigateur n'utilise pas $fetch,
  // un jeton expiré afficherait le JSON d'erreur brut. On renouvelle la session avant d'ouvrir le lien.
  document.addEventListener('click', async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
      return
    }

    const link = (event.target as Element | null)?.closest?.('a[href^="/api/"]') as HTMLAnchorElement | null
    if (!link || !userStore.isLoggedIn || !isVolunteerRoute(router.currentRoute.value?.path)) {
      return
    }

    // Jeton absent (cookie expiré), expiré, ou sur le point de l'être (session renouvelable uniquement)
    const needsRenewal = isTokenExpired()
      || (!trust.isUntrustedSession() && msLeft() <= LINK_RENEW_MARGIN_MS)
    if (!needsRenewal) {
      return
    }

    event.preventDefault()
    try {
      await refreshSessionIfNeeded()
      await nextTick()
    }
    catch (error: any) {
      if (error?.message === 'SESSION_EXPIRED') {
        await redirectToLogin()
        return
      }
    }

    if (link.target === '_blank') {
      window.open(link.href, '_blank', 'noopener')
    }
    else {
      window.location.assign(link.href)
    }
  }, true)

  Object.assign(wrappedFetch, baseFetch)

  globalThis.$fetch = wrappedFetch
  nuxtApp.$fetch = wrappedFetch
})
